/**
 * How long ago something happened, in words.
 *
 * Exists because of a real piece of misdirection: the instances list showed
 * "Connected" for an instance whose server had been switched off the day
 * before. The status is not a live probe — it is whatever the last check saw,
 * and deliberately so, since probing every tenant on page load would spend a
 * customer's metered Acumatica API budget every time they opened a list.
 *
 * So the honest fix is not to check more often, it is to say WHEN we looked.
 * The age is the information; the reader can judge whether it still means
 * anything.
 */
export function describeAge(value: string | Date | null | undefined, now: Date = new Date()): string | null {
  if (!value) return null;

  const then = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(then.getTime())) return null;

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);

  // A clock skew or a just-written timestamp can land slightly in the future.
  // "in -3 seconds" helps nobody.
  if (seconds < 45) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  // Past a month, a date is more use than a count. "47 days ago" makes nobody
  // reach for a calendar less than the date itself does.
  return then.toLocaleDateString();
}
